import type { ReactNode } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { TrendChart } from "../components/Chart";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Insight } from "../components/Insight";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { TopBar } from "../components/TopBar";
import { age } from "../domain/body";
import { dayLabel, todayStr } from "../domain/date";
import { older } from "../domain/eating";
import {
  TANDEM_SECS,
  TESTS,
  TUG_LINE,
  dueIn,
  rangeFor,
  retestDue,
  testDays,
  testSeries,
  testsFor,
  type TestId,
} from "../domain/fitnessTests";
import { useDeviceAnswers, useKaya } from "../store/kayaStore";
import "./Progress.css";

const ICON: Record<TestId, ReactNode> = {
  chair: I.legs,
  balance: I.balance,
  tug: I.walk,
  step: I.footsteps,
  pushups: I.upper,
  plank: I.core,
};

const UNIT: Record<TestId, string> = {
  chair: "stands",
  balance: "s",
  tug: "s",
  step: "steps",
  pushups: "push-ups",
  plank: "s",
};

/** the board's row names: "Step test" where the protocol's title is "2-minute step" */
const NAME: Record<TestId, string> = {
  chair: TESTS.chair.title,
  balance: TESTS.balance.title,
  tug: TESTS.tug.title,
  step: "Step test",
  pushups: TESTS.pushups.title,
  plank: TESTS.plank.title,
};

const ORDER: TestId[] =["chair", "balance", "tug", "step", "pushups", "plank"];

/**
 * /progress/training/tests (board ProgressTrainingTests; ADR-054): the person's own readings over
 * time, each test's figure beside its range, the retest. The chair-stand count leads — the figure
 * a 70-year-old understands (plan §3.4). The readings are what the person did, nothing smoothed;
 * under two readings there is no chart, one sentence instead (SNIPPETS §18). Kcal never.
 */
export default function ProgressTrainingTests() {
  const S = useKaya((s) => s.S);
  const device = useDeviceAnswers();
  const now = new Date();
  const today = todayStr(now);
  const years = age(S, now);
  const sex = S.profile.sex;
  const old = older(S, now);
  const plan = testsFor(S, S.profile.training ?? {}, device, {}, now);
  const series = Object.fromEntries(ORDER.map((t) => [t, testSeries(S, t)])) as Record<TestId, { ds: string; value: number }[]>;
  const shown = ORDER.filter(
    (t) => series[t].length || plan.required.includes(t) || plan.optional.includes(t) || plan.left.some((l) => l.test === t)
  );
  const lead = series.chair.length ? "chair" : (ORDER.find((t) => series[t].length) ?? null);
  const due = retestDue(S);
  const days = testDays(S);
  const needs = old ? "two minutes, a chair and someone beside you" : "a chair, a wall and a timer";

  const leadPts = lead ? series[lead] : [];
  const last = leadPts.length ? leadPts[leadPts.length - 1] : null;
  const leadRange = lead ? rangeFor(lead, years, sex) : null;

  const subtitle = (t: TestId): string => {
    const s = series[t];
    const out = plan.left.find((l) => l.test === t);
    if (!s.length) {
      if (out) return `left out: ${out.why.split(":")[0].toLowerCase()}`;
      return plan.optional.includes(t) ? "optional · not done" : "not done yet";
    }
    const when = dayLabel(s[s.length - 1].ds);
    const r = rangeFor(t, years, sex);
    if (r) return `${when} · range ${r.low}–${r.high}`;
    if (t === "balance") return `heel to toe · the line is ${TANDEM_SECS} s`;
    if (t === "tug") return `${when} · the line is ${TUG_LINE} s`;
    return `${when} · your figure, no published range`;
  };
  const value = (t: TestId): string => {
    const s = series[t];
    if (!s.length) return "–";
    const v = s[s.length - 1].value;
    return UNIT[t] === "s" ? `${v} s` : String(v);
  };

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Tests" back={{ to: "/progress/training", label: "Back to Training" }} />
          <div className="pg">
            {lead && last ? (
              <Hero
                label={`${lead === "chair" ? "Chair stands in 30 seconds" : TESTS[lead].title} · ${dayLabel(last.ds)}`}
                figure={last.value}
                unit={UNIT[lead]}
                line={
                  leadRange
                    ? `The usual range for ${leadRange.who} of ${leadRange.band} is ${leadRange.low}–${leadRange.high} (${leadRange.source})`
                    : "Your figure; no published range"
                }
              />
            ) : (
              <Hero label="Your tests" figure={null} line={`Not done yet: ${needs}.`} />
            )}

            {lead ? (
              <RowGroup>
                {leadPts.length >= 2 ? (
                  <div className="pg-block">
                    <TrendChart
                      pts={leadPts.map((p) => [p.ds, p.value])}
                      band={leadRange ? [leadRange.low, leadRange.high] : undefined}
                      readings="readings"
                      ariaLabel={`${TESTS[lead].title} over time: ${leadPts.map((p) => `${dayLabel(p.ds)} ${p.value}`).join(", ")}`}
                    />
                  </div>
                ) : (
                  <Insight
                    first
                    insight={{
                      key: "tests.line",
                      text: due
                        ? `A line of your readings appears after the retest on ${dayLabel(due)}.`
                        : "A line of your readings appears from the second.",
                      basedOn: "1 reading so far",
                      wait: "after the retest",
                    }}
                  />
                )}
              </RowGroup>
            ) : null}

            <RowGroup heading={`Your readings${days ? ` · ${days === 1 ? "1 test day" : `${days} test days`}` : ""}`}>
              {shown.map((t) => (
                <NavRow key={t} kind="value" icon={ICON[t]} title={NAME[t]} subtitle={subtitle(t)} value={value(t)} />
              ))}
            </RowGroup>

            {ORDER.filter((t) => t !== lead && series[t].length >= 2).map((t) => {
              const r = rangeFor(t, years, sex);
              return (
                <RowGroup key={t} heading={`${TESTS[t].title} over time`}>
                  <div className="pg-block">
                    <TrendChart
                      pts={series[t].map((p) => [p.ds, p.value])}
                      band={r ? [r.low, r.high] : undefined}
                      readings="readings"
                      ariaLabel={`${TESTS[t].title} over time: ${series[t].map((p) => `${dayLabel(p.ds)} ${p.value}`).join(", ")}`}
                    />
                  </div>
                </RowGroup>
              );
            })}

            {plan.held ? null : (
              <RowGroup>
                <NavRow
                  icon={I.calendar}
                  title={due ? (due <= today ? "Retest due now" : `Retest due ${dayLabel(due)}`) : "Do the tests"}
                  subtitle={due && due > today ? `${dueIn(due, today)} · ${needs}` : needs}
                  to="/train/tests"
                />
              </RowGroup>
            )}

            <p className="pg-note">
              {old
                ? "US norms (Rikli & Jones; CDC STEADI) — none exist for India. Your own readings over time say more than the range."
                : "Your own readings over time; the published ranges start at 60, and the push-ups and plank have none."}
            </p>
          </div>
        </Screen>
      </IonContent>
    </IonPage>
  );
}
